import { Component, HostListener, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, EMPTY, Observable, catchError, combineLatest, concat, debounceTime, distinctUntilChanged, exhaustMap, filter, map, merge, of, pairwise, share, switchMap, tap, timer } from 'rxjs';

import { UseMockEffects } from 'src/app/globals';
import { EffectsRepository } from '~modules/configurator/store/effects/effects.repository';
import { ChangeStatusData, EffectStatusData } from '~shared/types/websockets.types';
import { WebsocketService } from '~core/services/websockets.service';
import { EffectStatusService } from '~core/services/effect-status.service';

const EFFECT_TYPE_LABELS: Record<string, string> = {
	UPDATE_BOMITEMS: 'Bijwerken BOM items in configurator',
	SYNC_BOM_ODOO: 'Bijwerken stuklijst in Odoo',
	SYNC_ITEM_ODOO: 'Item details bijwerken in Odoo',
	SYNC_PART_ODOO: 'Part details bijwerken in Odoo',
	SYNC_PROJECT_ODOO: 'Project bijwerken in Odoo',
	SYNC_CUTTINGLIST_ODOO: 'Zaaglijst details bijwerken in Odoo',
	SYNC_CUTTINGLISTLINE_ODOO: 'Zaaglijstlijn details bijwerken in Odoo',
	STORE_ITEM_PRODUCT_IMAGE: 'Item afbeelding genereren',
	STORE_PART_PRODUCT_IMAGE: 'Part afbeelding genereren',
	SYNC_ITEM_IMAGE_ODOO: 'Item afbeelding bijwerken in Odoo',
	SYNC_PART_IMAGE_ODOO: 'Part afbeelding bijwerken in Odoo',
};

const MOCK_CHANGES: ChangeStatusData[] = [
	{
		correlationId: '01JMOCK0001',
		description: 'Updating item "Side panel"',
		originator: 'ARTICLE',
		createdAt: new Date(Date.now() - 30000).toISOString(),
		effects: [
			{
				correlationId: '01JMOCK0001',
				effectId: '01JMOCK_E001',
				effectType: 'SYNC_BOM_ODOO',
				status: 'PENDING',
				registeredBy: 'ARTICLE',
				registeredAt: new Date().toISOString(),
			},
			{
				correlationId: '01JMOCK0001',
				effectId: '01JMOCK_E002',
				effectType: 'UPDATE_BOMITEMS',
				status: 'COMPLETE',
				registeredBy: 'ARTICLE',
				registeredAt: new Date(Date.now() - 30000).toISOString(),
				completedAt: new Date(Date.now() - 5000).toISOString(),
			},
		],
	},
	{
		correlationId: '01JMOCK0002',
		description: 'Deleting item "Back panel"',
		originator: 'ARTICLE',
		createdAt: new Date(Date.now() - 60000).toISOString(),
		effects: [
			{
				correlationId: '01JMOCK0002',
				effectId: '01JMOCK_E003',
				effectType: 'UPDATE_BOMITEMS',
				status: 'IN_PROGRESS',
				registeredBy: 'ARTICLE',
				registeredAt: new Date(Date.now() - 60000).toISOString(),
			},
			{
				correlationId: '01JMOCK0002',
				effectId: '01JMOCK_E004',
				effectType: 'SYNC_BOM_ODOO',
				status: 'PENDING',
				registeredBy: 'ARTICLE',
				registeredAt: new Date(Date.now() - 60000).toISOString(),
			},
		],
	},
	{
		correlationId: '01JMOCK0003',
		description: 'Updating cutting list line "600x400"',
		originator: 'CUTTINGLIST',
		createdAt: new Date(Date.now() - 90000).toISOString(),
		effects: [
			{
				correlationId: '01JMOCK0003',
				effectId: '01JMOCK_E005',
				effectType: 'UPDATE_BOMITEMS',
				status: 'FAILED',
				registeredBy: 'CUTTINGLIST',
				registeredAt: new Date(Date.now() - 90000).toISOString(),
				completedAt: new Date(Date.now() - 80000).toISOString(),
				errorMessage: 'Board not found for material',
			},
		],
	},
];

@UntilDestroy()
@Component({
	selector: 'app-effect-status',
	templateUrl: './effect-status.component.html',
})
export class EffectStatusComponent implements OnInit, OnChanges {
	@Input() projectId: string;

	isOpen = false;
	loaded$: Observable<boolean>;
	changes$: Observable<ChangeStatusData[]>;
	hasActiveEffects$: Observable<boolean>;
	statusCounts$: Observable<{ status: string; count: number; label: string }[]>;
	pulsing$: Observable<boolean>;

	private projectId$ = new BehaviorSubject<string | null>(null);
	private pulsingUntil$ = new BehaviorSubject<number>(0);
	private isClicked = false;

	constructor(
		private effectsRepository: EffectsRepository,
		private websocketService: WebsocketService,
		private effectStatusService: EffectStatusService,
	) {}

	@HostListener('click')
	onHostClick(): void {
		this.isClicked = true;
	}

	@HostListener('document:click')
	onDocumentClick(): void {
		if (!this.isClicked) {
			this.isOpen = false;
		}
		this.isClicked = false;
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['projectId']) {
			this.projectId$.next(this.projectId);
		}
	}

	ngOnInit(): void {
		const activeProjectId$ = this.projectId$.pipe(
			filter((id): id is string => !!id),
			distinctUntilChanged(),
			tap(() => this.effectsRepository.reset())
		);

		if (UseMockEffects) {
			this.loadMockChanges();
		} else {
			activeProjectId$.pipe(
				switchMap(projectId => {
					const effects$ = this.websocketService.getEffectWSMessages$(projectId).pipe(
						tap(effect => {
							if (effect.status !== 'PENDING') {
								this.extendPulse();
							}
						}),
						// Apply every effect to the store the moment it arrives, so status-derived
						// UI (e.g. the price-syncing ellipsis) reacts instantly instead of waiting
						// for the debounced reconcile below. Capture whether the change was still
						// unknown at arrival so we know if a full refetch is still needed.
						map(effect => {
							const wasUnknown = !this.effectsRepository.hasChange(effect.correlationId);
							this.effectsRepository.upsertEffect(effect);
							return wasUnknown;
						}),
						share(),
					);

					// A single change fans out into several effects that arrive together.
					// The optimistic upsert above already reflects them; for changes we hadn't
					// seen yet, coalesce the burst into ONE status refetch to fill in the full
					// change metadata (description, sibling effects). The endpoint returns all
					// pending changes, so a single call covers the whole burst.
					const refetchOnUnknown$ = effects$.pipe(
						filter(wasUnknown => wasUnknown),
						debounceTime(350),
						// exhaustMap (not switchMap): while a refetch is in flight, ignore
						// further triggers instead of cancelling it. The GET returns the full
						// pending state, so the in-flight one already covers the burst.
						exhaustMap(() => this.effectStatusService.getPendingChanges(projectId).pipe(
							catchError(() => EMPTY),
						)),
						tap(changes => this.effectsRepository.setChanges(changes)),
					);

					// Effects that stop reporting leave this session's picture stuck on work the
					// backend may already have finished, which is why a reload clears it. This
					// asks the same question a reload does, without the reload.
					const refetchOnStalled$ = this.effectsRepository.reconcileNeeded$.pipe(
						exhaustMap(() => this.effectStatusService.getPendingChanges(projectId).pipe(
							catchError(() => EMPTY),
						)),
						tap(changes => this.effectsRepository.setChanges(changes)),
					);

					// Whatever the backend pushed while the socket was down never arrives: the
					// store would keep showing work as pending that has long since finished.
					// Each time the socket comes back up, the pending state is fetched afresh.
					const refetchOnReconnect$ = this.websocketService.wsConnectionState$.pipe(
						distinctUntilChanged(),
						pairwise(),
						filter(([, state]) => state === 'connected'),
						exhaustMap(() => this.effectStatusService.getPendingChanges(projectId).pipe(
							catchError(() => EMPTY),
						)),
						tap(changes => this.effectsRepository.setChanges(changes)),
					);

					return merge(
						concat(
							this.effectStatusService.getPendingChanges(projectId).pipe(
								tap(changes => this.effectsRepository.setChanges(changes)),
								catchError(() => EMPTY),
							),
							refetchOnUnknown$,
						),
						refetchOnStalled$,
						refetchOnReconnect$,
					);
				}),
				untilDestroyed(this),
			).subscribe();
		}

		this.loaded$ = this.effectsRepository.loaded$;
		this.changes$ = this.effectsRepository.changes$;

		const allEffects$ = this.changes$.pipe(
			map(changes => changes.flatMap(c => c.effects))
		);

		this.hasActiveEffects$ = allEffects$.pipe(
			map(effects => effects.some(e => e.status === 'PENDING' || e.status === 'IN_PROGRESS'))
		);

		this.statusCounts$ = allEffects$.pipe(
			map(effects => {
				const active = effects.filter(e => e.status === 'PENDING' || e.status === 'IN_PROGRESS').length;
				const complete = effects.filter(e => e.status === 'COMPLETE').length;
				const failed = effects.filter(e => e.status === 'FAILED').length;
				const skipped = effects.filter(e => e.status === 'NOT_IN_SCOPE').length;
				return [
					{ status: 'complete', count: complete, label: 'Synced' },
					{ status: 'active', count: active, label: 'Pending' },
					{ status: 'failed', count: failed, label: 'Failed' },
					{ status: 'skipped', count: skipped, label: 'Not in scope' },
				].filter(s => s.count > 0);
			})
		);

		this.pulsing$ = combineLatest([this.hasActiveEffects$, this.pulsingUntil$]).pipe(
			switchMap(([hasActive, pulsingUntil]) => {
				if (hasActive) return of(true);
				const remaining = pulsingUntil - Date.now();
				if (remaining > 0) {
					return concat(of(true), timer(remaining).pipe(map(() => false)));
				}
				return of(false);
			})
		);
	}

	toggle(): void {
		this.isOpen = !this.isOpen;
	}

	effectLabel(effectType: string): string {
		return EFFECT_TYPE_LABELS[effectType] || effectType;
	}

	effectFullLabel(effect: EffectStatusData): string {
		const base = this.effectLabel(effect.effectType);
		return effect.label ? `${base} — ${effect.label}` : base;
	}

	capitalize(value: string): string {
		return value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
	}

	deleteChange(event: Event, correlationId: string): void {
		event.stopPropagation();
		this.effectStatusService.deleteChange(correlationId).pipe(
			untilDestroyed(this),
		).subscribe(() => {
			this.effectsRepository.removeChange(correlationId);
		});
	}

	deleteAllChanges(event: Event): void {
		event.stopPropagation();
		this.effectsRepository.removeAllChanges();
		this.isOpen = false;
		this.effectStatusService.deleteAllChanges(this.projectId).pipe(
			untilDestroyed(this),
		).subscribe();
	}

	private loadMockChanges(): void {
		this.effectsRepository.setChanges(MOCK_CHANGES);

		timer(4000).pipe(untilDestroyed(this)).subscribe(() => {
			this.effectsRepository.upsertEffect({
				...MOCK_CHANGES[0].effects[0], status: 'COMPLETE', completedAt: new Date().toISOString(),
			});
			this.extendPulse();
		});
		timer(6000).pipe(untilDestroyed(this)).subscribe(() => {
			this.effectsRepository.upsertEffect({
				...MOCK_CHANGES[1].effects[0], status: 'COMPLETE', completedAt: new Date().toISOString(),
			});
			this.extendPulse();
		});
	}

	private extendPulse(): void {
		this.pulsingUntil$.next(Date.now() + 3000);
	}
}
